/**
 * KAN-845: шаг "Телефон" экрана "Начало работы": телефон и когда можно звонить.
 * Та же форма, что во вкладке "Профиль" (useManagerProfileForm, ProfileField,
 * WorkHoursEditor). Имя показывается, только если его ещё нет: без имени
 * профиль не сохранится. Телефон есть, а часов нет: шаг не сделан, над
 * пустым редактором часов подсказка.
 */
import type { FormEvent } from 'react';
import { CheckCircle2, Loader2 } from 'lucide-react';
import { PHONE_HINT, ProfileField } from '@/components/mobile/ProfileField';
import { WorkHoursEditor } from '@/components/managers/WorkHoursEditor';
import { useManagerProfileForm } from '@/hooks/useManagerProfile';
import { ONBOARDING_TEXTS } from '@/lib/managerOnboarding';
import { hasWorkHours, WORK_HOURS_LABEL } from '@/lib/workHours';

const ID_PREFIX = 'onboarding';

export function OnboardingPhoneForm(): JSX.Element | null {
  const form = useManagerProfileForm();
  const { draft, errors, setField, profile } = form;
  if (!profile) return null;
  const hoursMissing = profile.phone.trim() !== '' && !hasWorkHours(profile.work_hours);

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    void form.save();
  };

  return (
    <form onSubmit={onSubmit} noValidate className="space-y-3" data-testid="onboarding-phone-form">
      {!profile.first_name.trim() && (
        <ProfileField
          idPrefix={ID_PREFIX}
          name="first_name"
          label="Имя"
          value={draft.first_name}
          onChange={(v) => setField('first_name', v)}
          error={errors.first_name}
          autoComplete="given-name"
          maxLength={100}
        />
      )}
      <ProfileField
        idPrefix={ID_PREFIX}
        name="phone"
        label="Телефон"
        type="tel"
        inputMode="tel"
        value={draft.phone}
        onChange={(v) => setField('phone', v)}
        error={errors.phone}
        hint={PHONE_HINT}
        autoComplete="tel"
        placeholder="+7 900 000-00-00"
        maxLength={32}
      />
      {hoursMissing && (
        <p className="text-sm font-medium text-amber-700" data-testid="onboarding-hours-missing">
          {ONBOARDING_TEXTS.phoneHoursMissing}
        </p>
      )}
      <WorkHoursEditor
        label={WORK_HOURS_LABEL}
        value={draft.workHours}
        onChange={form.setWorkHours}
        timezone={profile.organization_timezone}
        error={errors.work_hours}
        disabled={form.saving}
      />
      {form.formError && (
        <p role="alert" className="text-sm text-red-600">
          {form.formError}
        </p>
      )}
      {form.saved && (
        <p role="status" className="flex items-center gap-1.5 text-sm text-green-700">
          <CheckCircle2 className="h-4 w-4" aria-hidden="true" />
          Сохранено
        </p>
      )}
      <button
        type="submit"
        disabled={form.saving}
        className="flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-blue-600 text-base font-medium text-white active:bg-blue-700 disabled:opacity-60"
      >
        {form.saving && <Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" />}
        {ONBOARDING_TEXTS.phoneSave}
      </button>
    </form>
  );
}
